import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../api';
import styles from './StripeCheckout.module.css';

/**
 * WalletFundForm — handles wallet top-up.
 *
 * In production:
 *   1. Import loadStripe from @stripe/stripe-js
 *   2. Import Elements, CardElement, useStripe, useElements from @stripe/react-stripe-js
 *   3. Replace the mock flow below with real Stripe card collection
 *
 * For demo/dev:
 *   Calls /api/wallet/fund/confirm directly (simulates payment success)
 */

const PRESETS = [
  { label: '$5',  cents: 500  },
  { label: '$10', cents: 1000 },
  { label: '$25', cents: 2500 },
  { label: '$50', cents: 5000 },
];

export function WalletFundForm({ onSuccess }) {
  const { updateWalletBalance } = useAuth();
  const { success, error: toastError } = useToast();

  const [selected, setSelected] = useState(1000);  // default $10
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('amount'); // amount | payment | done

  const amount = useCustom
    ? Math.max(100, Math.round(parseFloat(custom || '0') * 100))
    : selected;

  const handleProceed = () => {
    if (amount < 100) { toastError('Minimum amount is $1'); return; }
    setStep('payment');
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      /**
       * PRODUCTION:
       * const stripe = useStripe();
       * const elements = useElements();
       * const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
       *   payment_method: { card: elements.getElement(CardElement) }
       * });
       * if (error) throw new Error(error.message);
       * await api.confirmFund({ amountCents: amount, stripeChargeId: paymentIntent.id });
       */

      // DEMO: skip Stripe, call confirm directly
      const result = await api.confirmFund({ amountCents: amount });
      updateWalletBalance(result.newBalance);
      success(`$${(amount / 100).toFixed(2)} added to your wallet!`);
      setStep('done');
      onSuccess?.(result.newBalance);
    } catch (err) {
      toastError(err.message || 'Payment failed — please try again');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className={styles.done}>
        <span className={styles.doneIcon}>✓</span>
        <p className={styles.doneText}>
          <strong>${(amount / 100).toFixed(2)}</strong> added to your wallet
        </p>
        <button className="btn btn-outline-magenta btn-sm" onClick={() => setStep('amount')}>
          Add More
        </button>
      </div>
    );
  }

  return (
    <div className={styles.form}>
      {step === 'amount' && (
        <>
          <h3 className={styles.formTitle}>Choose an amount</h3>

          <div className={styles.presets}>
            {PRESETS.map(p => (
              <button
                key={p.cents}
                className={`${styles.preset} ${!useCustom && selected === p.cents ? styles.presetActive : ''}`}
                onClick={() => { setSelected(p.cents); setUseCustom(false); }}
              >
                {p.label}
              </button>
            ))}
            <button
              className={`${styles.preset} ${useCustom ? styles.presetActive : ''}`}
              onClick={() => setUseCustom(true)}
            >
              Custom
            </button>
          </div>

          {useCustom && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Amount ($)</label>
              <input
                className="form-input"
                type="number"
                min="1"
                step="1"
                placeholder="Enter amount"
                value={custom}
                onChange={e => setCustom(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className={styles.rateNote}>
            <span className={styles.rateDot}>◈</span>
            ${(amount / 100).toFixed(2)} = {Math.floor(amount / 100)} vote{Math.floor(amount / 100) !== 1 ? 's' : ''}
          </div>

          <button
            className="btn btn-magenta"
            style={{ width: '100%', marginTop: 8 }}
            onClick={handleProceed}
            disabled={amount < 100}
          >
            Continue → Payment
          </button>
        </>
      )}

      {step === 'payment' && (
        <>
          <div className={styles.paymentHeader}>
            <button className={styles.backBtn} onClick={() => setStep('amount')}>
              ← Back
            </button>
            <h3 className={styles.formTitle}>Payment</h3>
          </div>

          <div className={styles.orderSummary}>
            <div className={styles.summaryRow}>
              <span>Wallet top-up</span>
              <span>${(amount / 100).toFixed(2)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Votes added</span>
              <span>{Math.floor(amount / 100)}</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
              <span>Total</span>
              <span>${(amount / 100).toFixed(2)}</span>
            </div>
          </div>

          {/* PRODUCTION: Replace with <CardElement /> from @stripe/react-stripe-js */}
          <div className={styles.cardMock}>
            <div className={styles.cardMockInner}>
              <p className={styles.cardMockLabel}>Card details</p>
              <div className={styles.cardMockField}>
                <input
                  className="form-input"
                  placeholder="4242 4242 4242 4242"
                  disabled
                  style={{ opacity: 0.5 }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <input className="form-input" placeholder="MM / YY" disabled style={{ opacity: 0.5 }} />
                <input className="form-input" placeholder="CVC" disabled style={{ opacity: 0.5 }} />
              </div>
              <p className={styles.demoNote}>
                Demo mode — payment simulated. Connect Stripe for real transactions.
              </p>
            </div>
          </div>

          <button
            className="btn btn-magenta"
            style={{ width: '100%', marginTop: 8 }}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading
              ? 'Processing…'
              : `Pay $${(amount / 100).toFixed(2)}`
            }
          </button>

          <p className={styles.secureNote}>
            🔒 Payments secured by Stripe
          </p>
        </>
      )}
    </div>
  );
}
